<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    default: null,
  },
  teamId: {
    type: Number,
    default: null,
  },
  selectedWaTemplate: {
    type: Object,
    default: null,
  },
  useTemplate: {
    type: Boolean,
    default: false,
  },
  inboxOptions: {
    type: Array,
    required: true,
  },
  teamOptions: {
    type: Array,
    required: true,
  },
  validator: {
    type: Object,
    required: true,
  },
  totalSteps: {
    type: Number,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  inboxError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:inboxId',
  'update:teamId',
  'toggleTemplateMode',
  'pickTemplate',
  'pickerBackOrCancel',
  'back',
  'next',
]);

const { t } = useI18n();

const selectedTemplateBody = computed(() => {
  if (!props.selectedWaTemplate) return '';

  const { components } = props.selectedWaTemplate;
  if (!components) return '';

  const bodyComponent = components.find(c => c.type === 'BODY');
  return bodyComponent ? bodyComponent.text : '';
});

const handleNext = () => {
  props.validator.$touch();
  if (
    props.validator.$invalid ||
    (!props.selectedWaTemplate && !props.useTemplate)
  ) {
    return;
  }

  emit('next');
};

const localInboxId = computed({
  get: () => props.inboxId,
  set: value => emit('update:inboxId', value),
});

const localTeamId = computed({
  get: () => props.teamId,
  set: value => emit('update:teamId', value),
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEPS.INBOX_AND_TEMPLATE') }}
      </h3>
      <div class="flex items-center gap-2">
        <div class="text-xs text-n-slate-10">
          {{
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEP', {
              current: currentStep + 1,
              total: totalSteps,
            })
          }}
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="localInboxId"
        :options="inboxOptions"
        :has-error="!!inboxError"
        :placeholder="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="inboxError"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1 mt-4">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEAM.LABEL') }}
      </label>
      <ComboBox
        id="team"
        v-model="localTeamId"
        :options="teamOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEAM.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>
    <div class="flex flex-col gap-1 mt-4">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{
          t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEMPLATE.PICKER.USE_TEMPLATE')
        }}
      </label>

      <div v-if="!selectedWaTemplate" class="flex">
        <Button
          variant="solid"
          color="neutral"
          type="button"
          :disabled="!inboxId"
          :label="
            t(
              'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEMPLATE.PICKER.SELECT_TEMPLATE'
            )
          "
          class="text-sm"
          @click.stop="emit('toggleTemplateMode', $event)"
        />
      </div>

      <div
        v-if="useTemplate || selectedWaTemplate"
        class="border p-4 rounded-md bg-n-alpha-black2"
        @click.stop
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-sm font-medium">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEMPLATE.PICKER.TITLE') }}
          </h3>
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="
              selectedWaTemplate
                ? t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.BACK')
                : t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEMPLATE.PICKER.CANCEL')
            "
            class="text-sm"
            @click.stop="emit('pickerBackOrCancel')"
          />
        </div>
        <div v-if="selectedWaTemplate" class="mt-2 text-sm text-n-slate-12">
          <p class="font-semibold">{{ selectedWaTemplate.name }}</p>
          <p class="mt-1 text-xs whitespace-pre-wrap">
            {{ selectedTemplateBody }}
          </p>
        </div>
        <TemplatesPicker
          v-if="useTemplate && !selectedWaTemplate && inboxId"
          :inbox-id="inboxId"
          @on-select="template => emit('pickTemplate', template)"
        />
        <div
          v-else-if="useTemplate && !selectedWaTemplate && !inboxId"
          class="p-4 text-center"
        >
          <p class="text-sm text-n-slate-10">
            {{
              t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEMPLATE.SELECT_INBOX_FIRST')
            }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex justify-between mt-4">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.BACK')"
        @click="emit('back')"
      />
      <Button
        variant="solid"
        color="primary"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.NEXT')"
        :disabled="validator.$invalid || !selectedWaTemplate"
        @click="handleNext"
      />
    </div>
  </div>
</template>
